<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { CoreTypes, type ObjectRecord } from '@vp/shared-types';
import { applyFixes, fixToEdit, formatDiagnostic, isSupportedDiagramVersion, LANG_VERSION, safeCorrections, type Diagnostic, type SourceEdit } from '@vp/dskhema';
import { diagramPrimitives, drawPrimitive, INK_FILTER, layoutDiagram } from '@vp/render';
import { canvasDark } from '../theme';
import { live, ui } from '../editor/bridge';
import { cheatsheet, highlight, template, TEMPLATES } from '../editor/diagram';
import { ask } from '../dialogs';
import { locale, t } from '../i18n';
import Icon from './Icon.vue';

/**
 * DSkhema code window (docs/ru/DSKHEMA.md §4): code with highlighting and line numbers on the left,
 * a live preview on the right; the canvas changes only on "Done" (one undo step).
 */
const text = ref(ui.diagramEdit?.source ?? '');
const initial = text.value;
const area = ref<HTMLTextAreaElement | null>(null);
const hl = ref<HTMLElement | null>(null);
const gutter = ref<HTMLElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);
const previewBox = ref<HTMLElement | null>(null);
const showCheats = ref(false);
const lastGood = ref(text.value);

const ctx = computed(() => ({ shapes: live.editor?.scene.displayCtx.diagramShapes, locale: locale.value }));
const parsed = computed(() => layoutDiagram(text.value, ctx.value).parsed);
const diagnostics = computed<Diagnostic[]>(() => parsed.value.diagnostics);
const errors = computed(() => diagnostics.value.filter((d) => d.severity === 'error'));
const warnings = computed(() => diagnostics.value.filter((d) => d.severity === 'warning'));
const lineCount = computed(() => text.value.split('\n').length);
const html = computed(() => highlight(text.value, diagnostics.value) + '\n');
const badLines = computed(() => {
  const m = new Map<number, Diagnostic>();
  for (const d of diagnostics.value) if (!m.has(d.line) || d.severity === 'error') m.set(d.line, d);
  return m;
});

// the preview keeps the last source that laid out without errors
watch(
  text,
  (v) => {
    const r = layoutDiagram(v, ctx.value);
    if (r.geometry && !r.parsed.diagnostics.some((d) => d.severity === 'error')) lastGood.value = v;
    schedulePreview();
  },
  { flush: 'post' },
);
const showingStale = computed(() => errors.value.length > 0 && lastGood.value !== text.value);

let raf = 0;
function schedulePreview(): void {
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(drawPreview);
}

function drawPreview(): void {
  const c = canvas.value;
  const box = previewBox.value;
  if (!c || !box) return;
  const src = errors.value.length ? lastGood.value : text.value;
  const { geometry } = layoutDiagram(src, ctx.value);
  const dpr = window.devicePixelRatio || 1;
  const W = box.clientWidth;
  const H = box.clientHeight;
  c.width = Math.max(1, Math.round(W * dpr));
  c.height = Math.max(1, Math.round(H * dpr));
  c.style.width = W + 'px';
  c.style.height = H + 'px';
  const g = c.getContext('2d')!;
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, c.width, c.height);
  if (!geometry) return;
  const pad = 24;
  const s = Math.min(1.25, (W - pad * 2) / geometry.width, (H - pad * 2) / geometry.height);
  const obj = { id: ui.diagramEdit?.id ?? 'preview', type: CoreTypes.diagram, x: 0, y: 0, w: geometry.width, h: geometry.height, rotation: 0, props: { source: src, lang: 'dskhema', langVersion: LANG_VERSION }, style: { fill: { kind: 'none' }, stroke: null, opacity: 1 } } as unknown as ObjectRecord;
  const prims = diagramPrimitives(obj, { ...ctx.value, modules: live.editor?.scene.displayCtx.modules, formatVersion: live.editor?.scene.displayCtx.formatVersion });
  const ox = (W - geometry.width * s) / 2;
  const oy = Math.max(pad, (H - geometry.height * s) / 2);
  g.setTransform(dpr * s, 0, 0, dpr * s, ox * dpr, oy * dpr);
  for (const p of prims) drawPrimitive(g, p, 1, s * dpr, { images: { get: () => null }, textLodPx: 0 });
}

function syncScroll(): void {
  const a = area.value;
  if (!a) return;
  if (hl.value) {
    hl.value.scrollTop = a.scrollTop;
    hl.value.scrollLeft = a.scrollLeft;
  }
  if (gutter.value) gutter.value.scrollTop = a.scrollTop;
}

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    apply();
    return;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    void cancel();
    return;
  }
  const ta = area.value;
  if (ta && e.target === ta && e.key === ' ' && ta.selectionStart === autoSpaceAt && ta.selectionEnd === autoSpaceAt) {
    e.preventDefault();
    autoSpaceAt = -1;
    return;
  }
  if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Enter') autoSpaceAt = -1;
  if (e.target === area.value && pairKey(e)) return;
  if (e.key === 'Tab' && area.value) {
    // two spaces instead of leaving the editor
    e.preventDefault();
    const a = area.value;
    const st = a.selectionStart;
    text.value = text.value.slice(0, st) + '  ' + text.value.slice(a.selectionEnd);
    void nextTick(() => a.setSelectionRange(st + 2, st + 2));
  }
}

/**
 * Edit the code the way typing does (execCommand keeps the textarea's own undo): a correction is
 * undone with one Ctrl+Z, like a typed word.
 */
function replaceRange(e: SourceEdit): void {
  const a = area.value;
  if (!a) return;
  a.focus();
  a.setSelectionRange(e.from, e.to);
  if (!document.execCommand('insertText', false, e.text)) {
    // no execCommand (very old engines): plain replacement
    text.value = text.value.slice(0, e.from) + e.text + text.value.slice(e.to);
  }
}

/** Caret position right after a space a correction added: a space typed there is swallowed. */
let autoSpaceAt = -1;

/** Safe corrections right while typing or pasting: typographic arrows become the language's ones. */
function onInput(e: Event): void {
  const ie = e as InputEvent;
  if (!ie.inputType?.startsWith('insert')) return;
  const a = area.value;
  if (!a) return;
  const edits = safeCorrections(a.value);
  if (!edits.length) return;
  let caret = a.selectionStart;
  for (const ed of [...edits].sort((x, y) => y.from - x.from)) {
    replaceRange(ed);
    if (ed.to <= caret) caret += ed.text.length - (ed.to - ed.from);
  }
  a.setSelectionRange(caret, caret);
  autoSpaceAt = a.value[caret - 1] === ' ' ? caret : -1;
}

const fixable = computed(() => diagnostics.value.filter((d) => d.fix));
/** A suggested correction, confirmed by the user. */
function applyFix(d: Diagnostic): void {
  if (d.fix) replaceRange(fixToEdit(text.value, d.fix));
}
/** All suggestions at once — still one undo step. */
function applyAll(): void {
  const next = applyFixes(text.value, fixable.value.map((d) => d.fix!));
  if (next !== text.value) replaceRange({ from: 0, to: text.value.length, text: next });
}
const fixLabel = (d: Diagnostic) => {
  const t0 = d.fix!.text.trim();
  return t0 ? t('diagram.fixTo', { text: t0.length > 30 ? t0.slice(0, 30) + '…' : t0 }) : t('diagram.fix');
};

/** Paired quotes and brackets: typing an opening one adds the closing one, typing it over skips it. */
function pairKey(e: KeyboardEvent): boolean {
  const a = area.value;
  if (!a || e.ctrlKey || e.metaKey || e.altKey) return false;
  const st = a.selectionStart;
  const en = a.selectionEnd;
  const next = text.value[st];
  if ((e.key === ']' || e.key === '"') && st === en && next === e.key) {
    e.preventDefault();
    a.setSelectionRange(st + 1, st + 1);
    return true;
  }
  if ((e.key === '[' || e.key === '"') && st === en && (!next || /\s|[\],:]/.test(next))) {
    // a quote right after a letter is probably closing something: no pair then
    if (e.key === '"' && st > 0 && /[\p{L}\p{N}]/u.test(text.value[st - 1])) return false;
    e.preventDefault();
    replaceRange({ from: st, to: en, text: e.key === '[' ? '[]' : '""' });
    a.setSelectionRange(st + 1, st + 1);
    return true;
  }
  return false;
}

function goTo(d: Diagnostic): void {
  const a = area.value;
  if (!a) return;
  const lines = text.value.split('\n');
  let pos = 0;
  for (let i = 0; i < d.line - 1; i++) pos += lines[i].length + 1;
  a.focus();
  a.setSelectionRange(pos + d.col - 1, pos + Math.max(d.col, d.end - 1));
}

async function useTemplate(kind: keyof typeof TEMPLATES): Promise<void> {
  if (text.value.trim() && text.value !== initial && !(await ask(t('diagram.confirmTemplate')))) return;
  text.value = template(kind);
}

function apply(): void {
  const ed = live.editor;
  const req = ui.diagramEdit;
  if (!ed || !req) return;
  const src = text.value;
  if (req.id) {
    const outdated = !isSupportedDiagramVersion(ed.doc.get(req.id)?.props['langVersion']);
    if (src !== initial || outdated) ed.setDiagramSource(req.id, src);
  } else ed.createDiagram(src);
  ui.diagramEdit = null;
}

async function cancel(): Promise<void> {
  ui.diagramEdit = null;
}

let ro: ResizeObserver | null = null;
onMounted(() => {
  area.value?.focus();
  ro = new ResizeObserver(schedulePreview);
  if (previewBox.value) ro.observe(previewBox.value);
  schedulePreview();
});
onBeforeUnmount(() => {
  ro?.disconnect();
  cancelAnimationFrame(raf);
});
</script>

<template>
  <div class="dialog-backdrop dg-backdrop" @pointerdown.self="cancel">
    <div class="dg" role="dialog" aria-modal="true" :aria-label="t('diagram.editTitle')" @keydown="onKey">
      <header class="dg-head">
        <strong><Icon name="diagram" :size="16" />{{ t('diagram.editTitle') }}</strong>
        <div class="tpl" role="group" :aria-label="t('diagram.templates')">
          <span class="muted-s">{{ t('diagram.templates') }}:</span>
          <button class="pill s" @click="useTemplate('flow')">{{ t('diagram.tplFlow') }}</button>
          <button class="pill s" @click="useTemplate('sequence')">{{ t('diagram.tplSequence') }}</button>
          <button class="pill s" @click="useTemplate('blank')">{{ t('diagram.tplBlank') }}</button>
        </div>
        <button class="pill s" :class="{ active: showCheats }" :aria-pressed="showCheats" @click="showCheats = !showCheats"><Icon name="info" :size="14" />{{ t('diagram.cheatsheet') }}</button>
        <button class="icon-btn" :aria-label="t('common.close')" @click="cancel"><Icon name="close" :size="16" /></button>
      </header>

      <div class="dg-body">
        <section class="code">
          <div class="editor">
            <div ref="gutter" class="gutter" aria-hidden="true">
              <div v-for="n in lineCount" :key="n" class="ln" :class="badLines.get(n)?.severity" :title="badLines.get(n) ? formatDiagnostic(badLines.get(n)!, locale) : undefined">{{ n }}</div>
            </div>
            <div class="layers">
              <!-- eslint-disable-next-line vue/no-v-html -- escaped by highlight() -->
              <pre ref="hl" class="hl" aria-hidden="true" v-html="html"></pre>
              <textarea ref="area" v-model="text" class="src" spellcheck="false" autocapitalize="off" autocomplete="off" :aria-label="t('diagram.editTitle')" @scroll="syncScroll" @input="onInput"></textarea>
            </div>
          </div>
          <div class="problems" :class="{ ok: !diagnostics.length }">
            <p v-if="!diagnostics.length" class="ok-line"><span class="dot ok"></span>{{ t('diagram.noErrors') }}</p>
            <template v-else>
              <p class="summary">
                {{ t('diagram.errors', { n: errors.length }) }}<template v-if="warnings.length">, {{ t('diagram.warnings', { n: warnings.length }) }}</template>
                <button v-if="fixable.length > 1" class="fix-btn all" data-act="fix-all" @click="applyAll">{{ t('diagram.fixAll') }}</button>
              </p>
              <ul>
                <li v-for="(d, i) in diagnostics.slice(0, 30)" :key="i" :class="d.severity" @click="goTo(d)">
                  <span class="dot" :class="d.severity"></span><span class="where">{{ t('diagram.line', { n: d.line }) }}</span><span class="msg">{{ formatDiagnostic(d, locale) }}</span>
                  <button v-if="d.fix" class="fix-btn" data-act="fix" :title="fixLabel(d)" @click.stop="applyFix(d)">{{ fixLabel(d) }}</button>
                </li>
              </ul>
            </template>
          </div>
        </section>

        <section ref="previewBox" class="preview" :aria-label="t('diagram.preview')">
          <canvas ref="canvas" role="img" :aria-label="t('diagram.preview')" :style="{ filter: canvasDark ? INK_FILTER : undefined }"></canvas>
          <p v-if="showingStale" class="stale"><Icon name="warning" :size="13" />{{ t('diagram.stale') }}</p>
          <div v-if="showCheats" class="cheats">
            <div v-for="sec in cheatsheet()" :key="sec.title" class="cheat-sec">
              <h4>{{ sec.title }}</h4>
              <table>
                <thead>
                  <tr>
                    <th>{{ t('diagram.notation') }}</th>
                    <th>{{ t('diagram.meaning') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="[code, meaning] in sec.rows" :key="code">
                    <td><code>{{ code }}</code></td>
                    <td>{{ meaning }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      <footer class="dg-foot">
        <span class="muted-s">DSkhema · {{ t('diagram.applyHint') }}</span>
        <button class="pill" @click="cancel">{{ t('diagram.cancel') }}</button>
        <button class="pill primary" :disabled="!parsed.model" @click="apply"><Icon name="check" :size="14" />{{ t('diagram.apply') }}</button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.dg-backdrop {
  z-index: 2500;
  background: var(--backdrop);
}
.dg {
  width: min(1180px, calc(100vw - 32px));
  height: min(760px, calc(100vh - 32px));
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.25);
  overflow: hidden;
}
.dg-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px 10px 18px;
  border-bottom: 1px solid var(--border);
}
.dg-head strong {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  margin-right: auto;
}
.tpl {
  display: flex;
  align-items: center;
  gap: 6px;
}
.muted-s {
  font-size: 12px;
  color: var(--text-3);
}
.pill.s {
  height: 28px;
  padding: 0 10px;
}
.dg-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(340px, 44%) 1fr;
}
.code {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--border);
}
.editor {
  flex: 1;
  min-height: 0;
  display: flex;
  background: var(--code);
  font-family: ui-monospace, 'Cascadia Code', Consolas, monospace;
  font-size: 13px;
  line-height: 20px;
}
.gutter {
  flex: none;
  width: 44px;
  padding: 12px 8px 12px 0;
  overflow: hidden;
  text-align: right;
  color: var(--text-3);
  user-select: none;
  background: var(--panel-2);
}
.ln {
  height: 20px;
}
.ln.error {
  color: var(--danger);
  font-weight: 700;
}
.ln.warning {
  color: var(--warn);
  font-weight: 700;
}
.layers {
  position: relative;
  flex: 1;
  min-width: 0;
}
.hl,
.src {
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 12px 14px;
  font: inherit;
  line-height: inherit;
  white-space: pre;
  overflow: auto;
  tab-size: 2;
}
.hl {
  pointer-events: none;
  color: var(--text);
  --hl-kw: #7c3aed;
  --hl-op: #ea580c;
  --hl-form: #059669;
  --hl-sty: #0891b2;
  --hl-cls: #db2777;
  --hl-str: #a16207;
}
:root[data-theme='dark'] .hl {
  --hl-kw: #c4a5ff;
  --hl-op: #fb923c;
  --hl-form: #4ade80;
  --hl-sty: #67e8f9;
  --hl-cls: #f9a8d4;
  --hl-str: #fcd34d;
}
.src {
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  color: transparent;
  caret-color: var(--text);
}
.src::selection {
  background: rgba(37, 99, 235, 0.22);
  color: transparent;
}
.hl :deep(.t-kw) {
  color: var(--hl-kw);
  font-weight: 600;
}
.hl :deep(.t-op) {
  color: var(--hl-op);
  font-weight: 600;
}
.hl :deep(.t-form) {
  color: var(--hl-form);
}
.hl :deep(.t-sty) {
  color: var(--hl-sty);
}
.hl :deep(.t-cls) {
  color: var(--hl-cls);
}
.hl :deep(.t-str) {
  color: var(--hl-str);
}
.hl :deep(.t-lbl) {
  color: var(--text-2);
}
.hl :deep(.t-id) {
  color: var(--accent-text);
}
.hl :deep(.t-com) {
  color: var(--text-3);
  font-style: italic;
}
.hl :deep(.l-error) {
  text-decoration: underline wavy #ef4444;
  text-underline-offset: 4px;
}
.hl :deep(.l-warning) {
  text-decoration: underline wavy #f59e0b;
  text-underline-offset: 4px;
}
.problems {
  flex: none;
  max-height: 150px;
  overflow: auto;
  padding: 8px 14px 10px;
  border-top: 1px solid var(--border);
  font-size: 12px;
}
.ok-line,
.summary {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0 4px;
  color: var(--text-3);
}
.problems ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.problems li {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 3px 6px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--text-2);
}
.msg {
  flex: 1;
  min-width: 0;
}
.fix-btn {
  flex: none;
  margin-left: auto;
  height: 22px;
  padding: 0 10px;
  border: 1px solid var(--accent-border);
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-text);
  font: inherit;
  font-size: 11.5px;
  cursor: pointer;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fix-btn.all {
  margin-left: 10px;
}
.problems li:hover {
  background: var(--panel-2);
}
.where {
  flex: none;
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}
.dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  align-self: center;
}
.dot.ok {
  background: var(--ok);
}
.dot.error {
  background: var(--danger);
}
.dot.warning {
  background: #f59e0b;
}
.preview {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background:
    radial-gradient(circle, var(--border) 1px, transparent 1.2px) 0 0 / 18px 18px,
    var(--panel-2);
}
.preview canvas {
  display: block;
}
.stale {
  position: absolute;
  left: 12px;
  bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--warn-soft);
  color: var(--warn);
  font-size: 12px;
}
.cheats {
  position: absolute;
  inset: 10px;
  overflow: auto;
  padding: 14px 18px;
  background: var(--float);
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.12);
}
.cheat-sec h4 {
  margin: 4px 0 8px;
  font-size: 13px;
}
.cheat-sec table {
  width: 100%;
  margin: 0 0 16px;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  overflow: hidden;
}
.cheat-sec th {
  padding: 8px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-3);
  background: var(--seg);
}
.cheat-sec td {
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-2);
  vertical-align: top;
  border-top: 1px solid var(--border);
}
.cheat-sec td:first-child {
  width: 58%;
}
.cheat-sec code {
  font-family: ui-monospace, Consolas, monospace;
  font-size: 12px;
  color: var(--accent-text);
  white-space: pre-wrap;
}
.dg-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--border);
}
.dg-foot .muted-s {
  margin-right: auto;
}
.dg-foot .pill {
  height: 34px;
  padding: 0 16px;
  font-size: 13px;
}
.pill.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}
.pill.primary:hover:not(:disabled) {
  background: var(--accent-2);
  color: var(--on-accent);
}
@media (max-width: 760px) {
  .dg-body {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
  }
  .tpl {
    display: none;
  }
}
</style>
